import type { TabItem } from "@/hooks/use-tab-navigation";

import { Typography } from "antd";
import { useTranslation } from "react-i18next";

import CartesianIcon from "@/components/Dataset/Icons/CartesianIcon";
import FileIcon from "@/components/Dataset/Icons/FileIcon";
import GpsIcon from "@/components/Dataset/Icons/GpsIcon";
import ImageIcon from "@/components/Dataset/Icons/ImageIcon";
import PlanPositioningIcon from "@/components/Dataset/Icons/PlanPositioningIcon";
import { TAB_KEYS, TAB_PARAM_KEYS } from "@/constants/TabKeys";
import InterventionSectionCartesian from "@/modules/intervention/intervention-section-cartesian";
import InterventionSectionComments from "@/modules/intervention/intervention-section-comments";
import {
  INTERVENTION_SECTION_URL_PARAM_KEYS,
  useInterventionSectionContext,
} from "@/modules/intervention/intervention-section-context";
import { useInterventionSectionDatasets } from "@/modules/intervention/intervention-section-datasets";
import InterventionSectionEmpty from "@/modules/intervention/intervention-section-empty";
import InterventionSectionFile from "@/modules/intervention/intervention-section-file";
import InterventionSectionGps from "@/modules/intervention/intervention-section-gps";
import InterventionSectionImages from "@/modules/intervention/intervention-section-images";
import InterventionSectionPlanPositioning from "@/modules/intervention/intervention-section-plan-positioning";
import ModelIcon from "@/modules/model-icon";
import { NestedTabs } from "@/ui/nested-tabs";

import { INTERVENTION_SECTION_TABS_KEY } from "./intervention-section-tabs.constant";

import "./intervention-section-tabs.style.less";

const { Title } = Typography;

const InterventionSectionTabs = () => {
  const { t } = useTranslation();
  const { observationId } = useInterventionSectionContext();
  const { hasTemplate, hasDatasets } = useInterventionSectionDatasets();
  const hideComments = observationId ? false : true;

  const tabs: TabItem[] = [
    {
      key: "images",
      label: t("global:datasetTypes.images"),
      icon: <ImageIcon filled={hasDatasets("image")} />,
      children: <InterventionSectionImages />,
      hidden: !hasTemplate("image"),
    },
    {
      key: "cartesian",
      label: t("global:datasetTypes.cartesian"),
      icon: <CartesianIcon filled={hasDatasets("cartesian")} />,
      children: <InterventionSectionCartesian />,
      hidden: !hasTemplate("cartesian"),
    },
    {
      key: "gps",
      label: t("global:datasetTypes.gps"),
      icon: <GpsIcon filled={hasDatasets("gps")} />,
      children: <InterventionSectionGps />,
      hidden: !hasTemplate("gps"),
    },
    {
      key: "file",
      label: t("global:datasetTypes.file"),
      icon: <FileIcon filled={hasDatasets("file")} />,
      children: <InterventionSectionFile />,
      hidden: !hasTemplate("file"),
    },
    {
      key: "plan_positioning",
      label: t("global:datasetTypes.planPositioning"),
      icon: <PlanPositioningIcon filled={hasDatasets("planPositioning")} />,
      children: <InterventionSectionPlanPositioning />,
      hidden: !hasTemplate("planPositioning"),
    },
    {
      key: "comments",
      label: t("comment:plural"),
      icon: <ModelIcon model="comment" />,
      children: <InterventionSectionComments />,
      hidden: hideComments,
    },
  ];

  const activeTabs = tabs.filter((tab) => !tab.hidden);
  const hasActiveTabs = activeTabs.length > 0;
  const defaultActiveKey = activeTabs[0]?.key;

  return (
    <div className="intervention-section-tabs">
      {hasActiveTabs ? (
        <NestedTabs
          items={tabs}
          defaultActiveKey={defaultActiveKey}
          tabKey={INTERVENTION_SECTION_TABS_KEY}
          paramsToKeep={INTERVENTION_SECTION_URL_PARAM_KEYS}
          alwaysSyncSearchParams
          shouldSync={(searchParams) => {
            // Only sync `sub_tab` search param if the current page (aka
            // `tab` search param) is intervention details tab. Otherwise
            // don't sync, to make sure others page tabs are clickables.
            const pageTab = searchParams.get(TAB_PARAM_KEYS.CONTEXT_TAB_KEY);
            return !pageTab || pageTab === TAB_KEYS.INTERVENTIONS_DETAILS;
          }}
        />
      ) : (
        <div className="intervention-section-tabs-empty">
          <Title level={3}>
            {t("intervention:sectionEmpty.additionalInfoTitle")}
          </Title>
          <InterventionSectionEmpty type="tabs" />
        </div>
      )}
    </div>
  );
};

export default InterventionSectionTabs;
